import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import useRutter from '~hooks/useRutter/useRutter';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import {
  quickBooksMappingConnectionQueryKey,
  useQuickBooksMappingQueries,
} from './QuickBooksMapping/QuickBooksMappingQueryContext';

const I18N_PREFIX =
  'administration.integration.rutter.quick_books_online.advanced_configuration.child_companies';

export default function SharedConnectionSetting() {
  const { toasterStore } = useStores();
  const { updateConnection } = useRutter();
  const queryClient = useQueryClient();
  const { connection, connectionId, isLoading } = useQuickBooksMappingQueries();
  const isFeatureEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.qbSharedChildConnection,
  });
  const [isSaving, setIsSaving] = useState(false);

  if (!isFeatureEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ py: 2 }}>
        <LoadingSpinner isVisible={true} loadingIndicatorSize={16} />
      </Box>
    );
  }

  if (!connection || connection.managedByOwner) {
    return null;
  }

  const saveSharing = async (sharedWithChildCompanies: boolean) => {
    setIsSaving(true);

    try {
      const updatedConnection = await updateConnection(connection.id, {
        sharedWithChildCompanies,
      });

      if (updatedConnection) {
        queryClient.setQueryData(
          quickBooksMappingConnectionQueryKey(connectionId),
          updatedConnection,
        );
        toasterStore.push(
          alert(
            t('administration.configuration.configuration_saved'),
            AlertTypes.success,
          ),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
        {t(`${I18N_PREFIX}.title`)}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={connection.sharedWithChildCompanies}
            disabled={isSaving}
            onChange={(event) => void saveSharing(event.target.checked)}
          />
        }
        label={t(`${I18N_PREFIX}.share_label`)}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary', pl: 4 }}>
        {t(`${I18N_PREFIX}.description`)}
      </Typography>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}
